import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import { 
  Plus, 
  Trash2,
  Save,
  MapPin,
  Loader2,
  ExternalLink,
  Calendar as CalendarIcon,
  ChevronDown,
  Download,
  Search,
  Check,
  ArrowRight,
  ArrowLeft,
  RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ---------------------------------------------------------------
// KNHB status hook — polls /api/knhb/status elke 60s
// Returned status shape: {available, checked_at, message, last_ok_at, last_fail_at}
// ---------------------------------------------------------------
const useKnhbStatus = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const check = async () => {
    try {
      const res = await axios.get(`${API}/knhb/status`);
      setStatus(res.data);
    } catch {
      setStatus({ available: false, message: "KNHB status onbekend" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    check();
    const iv = setInterval(check, 60_000);
    return () => clearInterval(iv);
  }, []);

  return { status, loading, refresh: check };
};

// KNHB status badge — presenteert de status uit useKnhbStatus
// ---------------------------------------------------------------
const KnhbStatusBadge = ({ status, loading, onRefresh }) => {
  const dotClass = loading
    ? "bg-amber-400 animate-pulse"
    : status?.available
    ? "bg-emerald-500"
    : "bg-rose-500";
  const label = loading
    ? "KNHB status checken..."
    : status?.available
    ? "KNHB verbonden"
    : "KNHB tijdelijk niet bereikbaar";
  const bg = loading
    ? "bg-amber-50 border-amber-200 text-amber-800"
    : status?.available
    ? "bg-emerald-50 border-emerald-200 text-emerald-800"
    : "bg-rose-50 border-rose-200 text-rose-800";

  const lastOk = status?.last_ok_at
    ? new Date(status.last_ok_at).toLocaleString("nl-NL", { dateStyle: "short", timeStyle: "short" })
    : null;

  const tooltip = status?.available
    ? (status.message || label)
    : (
      (status?.message || label) +
      (lastOk ? `\nLaatst gelukt: ${lastOk}` : "\nNog nooit succesvol gesynchroniseerd")
    );

  return (
    <div
      className={cn(
        "inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-medium cursor-help",
        bg,
      )}
      title={tooltip}
      data-testid="knhb-status-badge"
      data-available={status?.available === true ? "true" : "false"}
      onClick={onRefresh}
    >
      <span className={cn("w-2 h-2 rounded-full", dotClass)} />
      <span>{label}</span>
      {!loading && !status?.available && lastOk && (
        <span className="text-[10px] text-rose-700/70 ml-1 hidden sm:inline">
          (laatst OK: {lastOk})
        </span>
      )}
    </div>
  );
};

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, "0"));

const TimePicker = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [hour, setHour] = useState("");
  const [minute, setMinute] = useState("");

  // Sync from value prop when popover opens
  const handleOpen = (isOpen) => {
    if (isOpen && value) {
      const [h, m] = value.split(":");
      setHour(h || "");
      setMinute(m || "");
    } else if (isOpen) {
      setHour("");
      setMinute("");
    }
    setOpen(isOpen);
  };

  const selectHour = (h) => {
    setHour(h);
    if (minute) {
      onChange(`${h}:${minute}`);
      setOpen(false);
    }
  };

  const selectMinute = (m) => {
    setMinute(m);
    if (hour) {
      onChange(`${hour}:${m}`);
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn("w-full h-9 justify-start text-left font-normal text-xs", !value && "text-slate-400")}
          data-testid={testId}
        >
          {value || <span className="italic">ntb</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[200px] p-2" align="start">
        {value && (
          <button
            onClick={() => { onChange(""); setHour(""); setMinute(""); setOpen(false); }}
            className="w-full text-xs text-slate-500 hover:text-red-600 hover:bg-red-50 rounded py-1 mb-2 transition-colors"
            data-testid={`${testId}-clear`}
          >
            Set to ntb
          </button>
        )}
        <div className="flex gap-2">
          <div className="flex-1">
            <p className="text-[10px] uppercase font-medium text-slate-400 mb-1 text-center">Hour</p>
            <div className="grid grid-cols-4 gap-0.5 max-h-[180px] overflow-y-auto">
              {HOURS.map((h) => (
                <button
                  key={h}
                  onClick={() => selectHour(h)}
                  className={cn(
                    "text-xs rounded py-1 hover:bg-slate-100 transition-colors",
                    hour === h && "bg-slate-900 text-white hover:bg-slate-800"
                  )}
                >
                  {h}
                </button>
              ))}
            </div>
          </div>
          <div className="w-px bg-slate-200" />
          <div className="flex-1">
            <p className="text-[10px] uppercase font-medium text-slate-400 mb-1 text-center">Min</p>
            <div className="grid grid-cols-2 gap-0.5">
              {MINUTES.map((m) => (
                <button
                  key={m}
                  onClick={() => selectMinute(m)}
                  className={cn(
                    "text-xs rounded py-1 hover:bg-slate-100 transition-colors",
                    minute === m && "bg-slate-900 text-white hover:bg-slate-800"
                  )}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

const KnhbImportDialog = ({ open, onClose, season, seasonPartId, onImported }) => {
  const [step, setStep] = useState(1); // 1=search club, 2=select team, 3=preview
  const [clubQuery, setClubQuery] = useState("");
  const [clubs, setClubs] = useState([]);
  const [selectedClub, setSelectedClub] = useState(null);
  const [teams, setTeams] = useState([]);
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [previewMatches, setPreviewMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  // Auto-fill club query from season and check for stored KNHB IDs
  useEffect(() => {
    if (open && season) {
      // If season already has KNHB IDs, skip to team selection or preview
      if (season.knhb_team_id && season.knhb_club_id) {
        setStep(3);
        loadPreview(season.knhb_team_id, season.knhb_club_id);
      } else {
        setStep(1);
        setClubQuery(season.club_name || "");
        setClubs([]);
        setSelectedClub(null);
        setTeams([]);
        setSelectedTeam(null);
        setPreviewMatches([]);
      }
    }
  }, [open, season]);

  const searchClubs = async () => {
    if (!clubQuery.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/knhb/search-clubs`, { query: clubQuery.trim() });
      setClubs(res.data.clubs);
      if (res.data.clubs.length === 0) toast.info("Geen clubs gevonden");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Zoeken mislukt");
    } finally {
      setLoading(false);
    }
  };

  const selectClub = async (club) => {
    setSelectedClub(club);
    setLoading(true);
    try {
      const res = await axios.get(`${API}/knhb/clubs/${club.id}/teams`);
      setTeams(res.data.teams);
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Teams laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  const selectTeam = (team) => {
    setSelectedTeam(team);
    loadPreview(team.id, selectedClub?.id || season.knhb_club_id || "");
  };

  const loadPreview = async (teamId, clubId) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/knhb/preview-matches`, {
        season_id: season.id,
        season_part_id: seasonPartId,
        knhb_team_id: teamId,
        knhb_club_id: clubId,
      });
      setPreviewMatches(res.data.matches);
      setStep(3);
      if (res.data.matches.length === 0) toast.info("Geen aankomende wedstrijden gevonden");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Wedstrijden ophalen mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const teamId = selectedTeam?.id || season.knhb_team_id;
      const clubId = selectedClub?.id || season.knhb_club_id || "";
      const res = await axios.post(`${API}/knhb/import-matches`, {
        season_id: season.id,
        season_part_id: seasonPartId,
        knhb_team_id: teamId,
        knhb_club_id: clubId,
      });
      toast.success(res.data.message);
      onImported();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Importeren mislukt");
    } finally {
      setImporting(false);
    }
  };

  const resetLink = () => {
    setStep(1);
    setClubQuery(season?.club_name || "");
    setClubs([]);
    setSelectedClub(null);
    setTeams([]);
    setSelectedTeam(null);
    setPreviewMatches([]);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] flex flex-col" data-testid="knhb-import-dialog">
        <DialogHeader>
          <DialogTitle>Importeer van KNHB</DialogTitle>
          <DialogDescription>
            {step === 1 && "Zoek je club in de KNHB database"}
            {step === 2 && `Selecteer het team van ${selectedClub?.name || "de club"}`}
            {step === 3 && `${previewMatches.length} wedstrijd(en) gevonden`}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto min-h-0">
          {/* Step 1: Search Club */}
          {step === 1 && (
            <div className="space-y-3">
              <form onSubmit={(e) => { e.preventDefault(); searchClubs(); }} className="flex gap-2">
                <Input
                  value={clubQuery}
                  onChange={(e) => setClubQuery(e.target.value)}
                  placeholder="Clubnaam zoeken..."
                  className="flex-1"
                  data-testid="knhb-club-search-input"
                />
                <Button type="submit" disabled={loading || !clubQuery.trim()} size="sm" data-testid="knhb-club-search-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
                </Button>
              </form>
              <div className="space-y-1">
                {clubs.map((club) => (
                  <button
                    key={club.id}
                    onClick={() => selectClub(club)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-between group"
                    data-testid={`knhb-club-${club.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{club.name}</p>
                      <p className="text-xs text-slate-500">{club.city || ""} {club.abbreviation ? `(${club.abbreviation})` : ""}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Select Team */}
          {step === 2 && (
            <div className="space-y-1">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : teams.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Geen teams gevonden</p>
              ) : (
                teams.map((team) => (
                  <button
                    key={team.id}
                    onClick={() => selectTeam(team)}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-slate-100 transition-colors flex items-center justify-between group"
                    data-testid={`knhb-team-${team.id}`}
                  >
                    <div>
                      <p className="text-sm font-medium text-slate-900">{team.name}</p>
                      <p className="text-xs text-slate-500">{team.category_name || team.type || ""}</p>
                    </div>
                    <ArrowRight className="w-4 h-4 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </button>
                ))
              )}
            </div>
          )}

          {/* Step 3: Preview Matches */}
          {step === 3 && (
            <div className="space-y-1">
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                </div>
              ) : previewMatches.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-8">Geen aankomende wedstrijden gevonden voor dit team</p>
              ) : (
                previewMatches.map((m, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2 rounded-lg bg-slate-50 text-sm">
                    <span className="text-xs text-slate-500 w-[70px] flex-shrink-0">
                      {m.date ? format(new Date(m.date), "dd/MM/yy") : "?"}
                    </span>
                    <Badge variant="outline" className={cn("text-[10px] w-8 justify-center flex-shrink-0", m.is_home ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-orange-50 text-orange-700 border-orange-200")}>
                      {m.is_home ? "T" : "U"}
                    </Badge>
                    <span className="text-slate-900 truncate flex-1">{m.opponent_club} {m.opponent_team}</span>
                    <span className="text-xs text-slate-400 w-[38px] flex-shrink-0 text-right">{m.start_time || "-"}</span>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 pt-3 border-t">
          <div>
            {step > 1 && (
              <Button variant="ghost" size="sm" onClick={resetLink} data-testid="knhb-back-btn">
                <ArrowLeft className="w-4 h-4 mr-1" />
                Opnieuw zoeken
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose} data-testid="knhb-cancel-btn">
              Annuleren
            </Button>
            {step === 3 && previewMatches.length > 0 && (
              <Button size="sm" onClick={handleImport} disabled={importing} className="bg-slate-900 hover:bg-slate-800" data-testid="knhb-import-btn">
                {importing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                {importing ? "Importeren..." : `${previewMatches.length} wedstrijden importeren`}
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const emptyMatch = (seasonId, seasonPartId) => ({
  id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
  date: "",
  is_home: true,
  opponent_club: "",
  opponent_team: "",
  shirt: "Home",
  gather_time: "",
  start_time: "",
  address: null,
  season_id: seasonId,
  season_part_id: seasonPartId,
  isNew: true
});

export const Matches = () => {
  const { seasonPartId: urlSeasonPartId } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [seasonPartId, setSeasonPartId] = useState(urlSeasonPartId);
  const [seasonPart, setSeasonPart] = useState(null);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [lookupLoading, setLookupLoading] = useState({});
  const [addressModal, setAddressModal] = useState(null);
  const [knhbOpen, setKnhbOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const { status: knhbStatus, loading: knhbLoading, refresh: refreshKnhbStatus } = useKnhbStatus();
  const knhbUnavailable = !knhbLoading && knhbStatus?.available === false;

  useEffect(() => {
    fetchData();
  }, [urlSeasonPartId]);

  const fetchData = async () => {
    try {
      // Get the single season
      const seasonsRes = await axios.get(`${API}/seasons`);
      if (seasonsRes.data.length === 0) {
        toast.error("Geen seizoen gevonden");
        navigate("/season");
        return;
      }
      
      const currentSeason = seasonsRes.data[0];
      setSeason(currentSeason);
      
      // Use URL param or default to first season part
      const partId = urlSeasonPartId || currentSeason.season_parts?.[0]?.id;
      if (!partId) {
        toast.error("Geen seizoensdelen gevonden");
        navigate("/season");
        return;
      }
      setSeasonPartId(partId);
      
      const part = currentSeason.season_parts?.find(p => p.id === partId);
      setSeasonPart(part);
      
      const matchesRes = await axios.get(`${API}/matches?season_id=${currentSeason.id}&season_part_id=${partId}`);
      
      // Ensure we have at least 12 rows
      const existingMatches = matchesRes.data.map(m => ({ ...m, isNew: false }));
      const emptyRows = Math.max(0, 12 - existingMatches.length);
      const emptyMatches = Array.from({ length: emptyRows }, () => 
        emptyMatch(currentSeason.id, partId)
      );
      
      setMatches([...existingMatches, ...emptyMatches]);
    } catch (error) {
      toast.error("Gegevens laden mislukt");
    } finally {
      setLoading(false);
    }
  };

  const handleSeasonPartChange = (partId) => {
    setSeasonPartId(partId);
    navigate(`/matches/${partId}`);
  };

  const handleSyncKnhb = async () => {
    if (!season?.knhb_team_id) return;
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/knhb/sync-matches`, {
        season_id: season.id,
        season_part_id: seasonPartId,
        knhb_team_id: season.knhb_team_id,
        knhb_club_id: season.knhb_club_id || "",
      });
      const { updated, added, changes } = res.data;
      if (updated === 0 && added === 0) {
        toast.success("Alles is al up-to-date");
      } else {
        toast.success(res.data.message);
        if (changes && changes.length > 0) {
          toast.info(changes.slice(0, 3).join("\n"), { duration: 6000 });
        }
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Synchroniseren mislukt");
    } finally {
      setSyncing(false);
    }
  };

  const handleChange = (index, field, value) => {
    const updated = [...matches];
    updated[index][field] = value;
    // Clear address when switching home/away since the location changes
    if (field === "is_home") {
      updated[index].address = null;
    }
    setMatches(updated);
    setHasChanges(true);
  };

  const handleAddRow = () => {
    setMatches([
      ...matches,
      emptyMatch(season.id, seasonPartId)
    ]);
  };

  const handleDelete = async (index) => {
    const match = matches[index];
    
    if (!match.isNew && match.id && !match.id.startsWith("new-")) {
      try {
        await axios.delete(`${API}/matches/${match.id}`);
        toast.success("Wedstrijd verwijderd");
      } catch (error) {
        toast.error("Wedstrijd verwijderen mislukt");
        return;
      }
    }
    
    const updated = matches.filter((_, i) => i !== index);
    setMatches(updated);
  };

  const handleLookupAddress = async (index) => {
    const match = matches[index];
    
    // For home games, use own club name; for away games, use opponent club
    const clubToLookup = match.is_home ? season.club_name : match.opponent_club;
    
    if (!clubToLookup?.trim()) {
      toast.error(match.is_home ? "Clubnaam ontbreekt in seizoensinstellingen" : "Voer eerst de naam van de tegenstander in");
      return;
    }

    setLookupLoading(prev => ({ ...prev, [index]: true }));
    try {
      // For home games, check if we already have a cached home address on the season
      if (match.is_home && season.home_address) {
        const updated = [...matches];
        updated[index].address = season.home_address;
        setMatches(updated);
        setHasChanges(true);
        toast.success("Thuisadres ingevuld");
        setLookupLoading(prev => ({ ...prev, [index]: false }));
        return;
      }

      const response = await axios.post(`${API}/address/lookup`, {
        club_name: clubToLookup
      });
      
      const updated = [...matches];
      updated[index].address = response.data;
      setMatches(updated);
      setHasChanges(true);

      // Cache home address on the season for future home games
      if (match.is_home) {
        try {
          await axios.put(`${API}/seasons/${season.id}/home-address`, response.data);
          setSeason(prev => ({ ...prev, home_address: response.data }));
        } catch (err) {
          // Non-critical: home-address cache mislukt, adres wordt niet opgeslagen voor hergebruik
          console.warn("home-address cache mislukte:", err);
        }
      }

      toast.success("Adres gevonden!");
    } catch (error) {
      toast.error("Adres opzoeken mislukt");
    } finally {
      setLookupLoading(prev => ({ ...prev, [index]: false }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Accept rows with just a date — the opponent can be filled in later
      // (users often plan tasks on a date before the KNHB fixture is confirmed).
      const validMatches = matches.filter(m => m.date);
      
      // Separate existing and new matches
      const existingMatches = validMatches.filter(m => !m.isNew && !m.id.startsWith("new-"));
      const newMatches = validMatches.filter(m => m.isNew || m.id.startsWith("new-"));
      
      // Update existing matches
      for (const match of existingMatches) {
        await axios.put(`${API}/matches/${match.id}`, {
          date: match.date,
          is_home: match.is_home,
          opponent_club: match.opponent_club,
          opponent_team: match.opponent_team,
          shirt: match.shirt,
          gather_time: match.gather_time,
          start_time: match.start_time,
          season_id: season.id,
          season_part_id: seasonPartId
        });
        
        // Update address if exists
        if (match.address) {
          await axios.put(`${API}/matches/${match.id}/address`, match.address);
        }
      }
      
      // Create new matches
      for (const match of newMatches) {
        const response = await axios.post(`${API}/matches`, {
          date: match.date,
          is_home: match.is_home,
          opponent_club: match.opponent_club,
          opponent_team: match.opponent_team,
          shirt: match.shirt,
          gather_time: match.gather_time,
          start_time: match.start_time,
          season_id: season.id,
          season_part_id: seasonPartId
        });
        
        // Update address if exists
        if (match.address) {
          await axios.put(`${API}/matches/${response.data.id}/address`, match.address);
        }
      }
      
      toast.success("Wedstrijden opgeslagen");
      setHasChanges(false);
      fetchData();
    } catch (error) {
      toast.error("Wedstrijden opslaan mislukt");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" data-testid="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="matches-page" className="space-y-8">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-blue-600 mb-1">
            {season?.club_name} / {season?.team_name}
          </p>
          <h1 
            className="text-4xl md:text-5xl font-bold tracking-tight uppercase text-slate-900"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Wedstrijden
          </h1>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="text-slate-500 mt-1 -ml-2 h-auto py-1 px-2">
                {seasonPart?.name || "Selecteer seizoensdeel"}
                <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {season?.season_parts?.map(part => (
                <DropdownMenuItem 
                  key={part.id}
                  onClick={() => handleSeasonPartChange(part.id)}
                  className={cn(part.id === seasonPartId && "bg-slate-100")}
                >
                  {part.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <KnhbStatusBadge status={knhbStatus} loading={knhbLoading} onRefresh={refreshKnhbStatus} />
          {season?.knhb_team_id && (
            <Button
              variant="outline"
              onClick={handleSyncKnhb}
              disabled={syncing || knhbUnavailable}
              title={knhbUnavailable ? "KNHB is momenteel niet bereikbaar" : undefined}
              className="border-emerald-200 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="knhb-sync-btn"
            >
              <RefreshCw className={cn("w-4 h-4 mr-1", syncing && "animate-spin")} />
              {syncing ? "Vernieuwen..." : "Vernieuw van KNHB"}
            </Button>
          )}
          <Button
            variant="outline"
            onClick={() => setKnhbOpen(true)}
            disabled={knhbUnavailable}
            title={knhbUnavailable ? "KNHB is momenteel niet bereikbaar" : undefined}
            className="border-blue-200 text-blue-700 hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="knhb-import-open-btn"
          >
            <Download className="w-4 h-4 mr-1" />
            Importeer van KNHB
          </Button>
          <Button
            variant="outline"
            onClick={handleAddRow}
            data-testid="add-match-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Wedstrijd toevoegen
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || !hasChanges}
            className="bg-slate-900 hover:bg-slate-800 min-w-[120px]"
            data-testid="save-matches-btn"
          >
            {saving ? (
              <div className="spinner w-4 h-4 border-white border-t-transparent" />
            ) : (
              <>
                <Save className="w-4 h-4 mr-1" />
                Opslaan
              </>
            )}
          </Button>
        </div>
      </div>

      {/* Matches Table */}
      <div className="overflow-x-auto">
        <Card className="bg-white border border-slate-200 rounded-xl shadow-sm min-w-[1200px]">
          <CardHeader className="bg-slate-50 border-b border-slate-200 py-3 px-4">
            <div className="grid grid-cols-12 gap-2">
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">Datum</div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">T/U</div>
              <div className="col-span-2 text-xs uppercase font-medium text-slate-500 tracking-wider">Tegenstander</div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">Team</div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">Tenue</div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">Verzamelen</div>
              <div className="col-span-1 text-xs uppercase font-medium text-slate-500 tracking-wider">Aanvang</div>
              <div className="col-span-3 text-xs uppercase font-medium text-slate-500 tracking-wider">Adres</div>
              <div className="col-span-1"></div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="divide-y divide-slate-100">
              {matches.map((match, index) => (
                <div 
                  key={match.id} 
                  className="grid grid-cols-12 gap-2 px-4 py-2 hover:bg-slate-50 transition-colors group items-center"
                  data-testid={`match-row-${index}`}
                >
                  {/* Date */}
                  <div className="col-span-1">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full h-9 justify-start text-left font-normal text-xs",
                            !match.date && "text-slate-400"
                          )}
                          data-testid={`match-date-${index}`}
                        >
                          <CalendarIcon className="mr-1 h-3 w-3" />
                          {match.date ? format(new Date(match.date), "dd/MM") : "Pick"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar
                          mode="single"
                          selected={match.date ? new Date(match.date) : undefined}
                          onSelect={(date) => handleChange(index, "date", date ? format(date, "yyyy-MM-dd") : "")}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Home/Away */}
                  <div className="col-span-1">
                    <Select 
                      value={match.is_home ? "home" : "away"} 
                      onValueChange={(v) => handleChange(index, "is_home", v === "home")}
                    >
                      <SelectTrigger className="h-9 text-xs" data-testid={`match-homeaway-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="home">Thuis</SelectItem>
                        <SelectItem value="away">Uit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Opponent Club */}
                  <div className="col-span-2">
                    <Input
                      value={match.opponent_club}
                      onChange={(e) => handleChange(index, "opponent_club", e.target.value)}
                      placeholder="Clubnaam"
                      className="h-9 text-xs"
                      data-testid={`match-opponent-club-${index}`}
                    />
                  </div>

                  {/* Opponent Team */}
                  <div className="col-span-1">
                    <Input
                      value={match.opponent_team}
                      onChange={(e) => handleChange(index, "opponent_team", e.target.value)}
                      placeholder="Team"
                      className="h-9 text-xs"
                      data-testid={`match-opponent-team-${index}`}
                    />
                  </div>

                  {/* Shirt */}
                  <div className="col-span-1">
                    <Select 
                      value={match.shirt} 
                      onValueChange={(v) => handleChange(index, "shirt", v)}
                    >
                      <SelectTrigger className="h-9 text-xs" data-testid={`match-shirt-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Home">Thuis</SelectItem>
                        <SelectItem value="Away">Uit</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Gather Time */}
                  <div className="col-span-1">
                    <TimePicker
                      value={match.gather_time}
                      onChange={(v) => handleChange(index, "gather_time", v)}
                      testId={`match-gather-time-${index}`}
                    />
                  </div>

                  {/* Start Time */}
                  <div className="col-span-1">
                    <TimePicker
                      value={match.start_time}
                      onChange={(v) => handleChange(index, "start_time", v)}
                      testId={`match-start-time-${index}`}
                    />
                  </div>

                  {/* Address */}
                  <div className="col-span-3 flex gap-1">
                    {match.address ? (
                      <div className="flex-1 flex items-center gap-1">
                        <Badge 
                          variant="outline" 
                          className="bg-emerald-50 text-emerald-700 border-emerald-200 cursor-pointer text-xs truncate max-w-[150px]"
                          onClick={() => setAddressModal(match.address)}
                          data-testid={`match-address-badge-${index}`}
                        >
                          <MapPin className="w-3 h-3 mr-1 flex-shrink-0" />
                          {match.address.city || "View"}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={() => handleLookupAddress(index)}
                          disabled={lookupLoading[index]}
                        >
                          Vernieuwen
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-xs flex-1"
                        onClick={() => handleLookupAddress(index)}
                        disabled={lookupLoading[index] || (!match.is_home && !match.opponent_club.trim())}
                        data-testid={`fetch-address-${index}`}
                      >
                        {lookupLoading[index] ? (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        ) : (
                          <MapPin className="w-3 h-3 mr-1" />
                        )}
                        Adres ophalen
                      </Button>
                    )}
                  </div>

                  {/* Delete */}
                  <div className="col-span-1 flex justify-end">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDelete(index)}
                      className="h-8 w-8 text-slate-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`delete-match-${index}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Address Modal */}
      <Dialog open={!!addressModal} onOpenChange={() => setAddressModal(null)}>
        <DialogContent data-testid="address-modal">
          <DialogHeader>
            <DialogTitle>Clubadres</DialogTitle>
            <DialogDescription>Wedstrijdlocatie details</DialogDescription>
          </DialogHeader>
          {addressModal && (
            <div className="space-y-4">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Straat:</span>{" "}
                  <span className="text-slate-900">{addressModal.street || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Postcode:</span>{" "}
                  <span className="text-slate-900">{addressModal.postal_code || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Plaats:</span>{" "}
                  <span className="text-slate-900">{addressModal.city || "N/B"}</span>
                </p>
                <p className="text-sm">
                  <span className="font-medium text-slate-500">Land:</span>{" "}
                  <span className="text-slate-900">{addressModal.country || "N/B"}</span>
                </p>
              </div>
              {addressModal.google_maps_url && (
                <Button
                  className="w-full"
                  onClick={() => window.open(addressModal.google_maps_url, "_blank")}
                  data-testid="view-on-maps-btn"
                >
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Bekijk op Google Maps
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* KNHB Import Dialog */}
      <KnhbImportDialog
        open={knhbOpen}
        onClose={() => setKnhbOpen(false)}
        season={season}
        seasonPartId={seasonPartId}
        onImported={fetchData}
      />
    </div>
  );
};
